import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Grid } from '@mui/material';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { LinePath, Line } from '@visx/shape';
import { localPoint } from '@visx/event';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  getParams,
  getMonth,
  thousandFormat,
  thousandFormat2,
  getMonths2,
  getMonths,
  financialMonth,
  printFormat
} from '../../../../Utils/Functions';
import {
  fMonths,
  enMonths,
  positiveActiveColor,
  negativeActiveColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

let tooltipTimeout;
const xSelector = (d) => d?.month;
const ySelector = (d) => d?.CashBalance;

const TopChart = ({
  topData,
  selectedYears,
  defaultStartMonth,
  defaultMonth,
  updateFilter,
  cashflowMiddleRequest,
  selectedTopItems,
  dimDate,
}) => {
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [data, setData] = useState([]);
  const [safetyLine, setSafetyLine] = useState(0);
  const [maxLine, setMaxLine] = useState(0);
  const [minLine, setMinLine] = useState(0);
  const [xTransform, setXTransform] = useState(0);
  const [X, setX] = useState(0);
  const [iX, setIX] = useState(0);
  const [iY, setIY] = useState(0);
  const [rX, setRX] = useState(0);
  const [rY, setRY] = useState(0);
  const [rW, setRW] = useState(0);
  const [rH, setRH] = useState(0);
  const [canvasIndex, setCanvasIndex] = useState(-1);
  const [dragFlag, setDragFlag] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const dragChartRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const isMountedRef = useRef(true);

  // Force re-render when window is resized
  const onResize = useCallback(() => {
    if (isMountedRef.current) {
      setResize(prev => !prev);
    }
  }, []);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dataArray = [];
    let _safetyLine = 0;
    let _maxLine = 0;
    let _minLine = 0;

    if (selectedYears.length === 0) return;

    selectedYears.forEach((year) => {
      dictData[year] = {};
      dictData[year]['months'] = [];
      dictData[year]['totals'] = [];
      dictData[year]['year'] = year;
      fMonths.forEach((month) => {
        dictData[year]['months'][month] = {};
        dictData[year]['months'][month]['month'] = month;
        dictData[year]['months'][month]['year'] = year;
        dictData[year]['months'][month]['CashBalance'] = 0;
        dictData[year]['months'][month]['SafetyBankBalance'] = 0;
        dictData[year]['totals'][month] = 0;
      });
    });

    let fiscalMonths = getMonths(defaultStartMonth);

    if (topData && topData.length > 0 && selectedYears.length > 0) {
      topData.forEach((item) => {
        let month = getMonth(item.Date);

        if (dictData[item.FY]) {
          if (
            dictData[item.FY]['months'][month] !== null &&
            dictData[item.FY]['months'][month] !== undefined
          ) {
            dictData[item.FY]['months'][month]['CashBalance'] = parseFloat(
              item.CashBalance,
            );
            dictData[item.FY]['months'][month]['SafetyBankBalance'] =
              parseFloat(item.SafetyBankBalance);
            dictData[item.FY]['totals'][month] = parseFloat(item.CashBalance);
          }
          _safetyLine = item.SafetyBankBalance
            ? parseFloat(item.SafetyBankBalance)
            : 0;
          _maxLine = Math.max(_maxLine, parseFloat(item.CashBalance));
          _minLine = Math.min(_minLine, parseFloat(item.CashBalance));
        }
      });

      selectedYears.forEach((year) => {
        let _newItem = [];
        fiscalMonths.forEach((month) => {
          _newItem.push(dictData[year]['months'][month]);
        });
        dictData[year]['months'] = _newItem;
        dataArray.push(dictData[year]);
      });
    }
    _maxLine = Math.max(_maxLine, _safetyLine);
    _minLine = Math.min(_minLine, _safetyLine);

    let xTransformValue = 0;
    let maxYValue = Math.max(Math.abs(_minLine), _maxLine);
    if (
      !isFinite(maxYValue) ||
      maxYValue === Number.NEGATIVE_INFINITY ||
      isNaN(maxYValue) ||
      maxYValue === 0
    )
      xTransformValue = 25;
    else {
      let labelLength =
        thousandFormat(maxYValue).length > thousandFormat(_minLine).length
          ? thousandFormat2(maxYValue).length
          : thousandFormat2(_minLine).length;
      if (parseInt(maxYValue) < 10) xTransformValue = 8 * (labelLength + 2);
      else xTransformValue = 8 * (labelLength + 1);
    }

    setData(dataArray);
    setSafetyLine(_safetyLine);
    setMaxLine(_maxLine);
    setMinLine(_minLine);
    setXTransform(xTransformValue);
  }, [topData, selectedYears, defaultStartMonth]);

  const deSelectAll = useCallback(() => {
    setHoverBar(null);
    updateFilter({
      selectedTopItems: [],
    });
  }, [updateFilter]);

  const showTooltipHandler = useCallback((event, bar, xScale, yScale, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);
    let dataObj = {},
      top,
      left,
      pTop,
      pLeft;

    let tmpX = [];
    let fiscalMonths = getMonths2(defaultMonth, defaultStartMonth);

    if (bar.months === undefined) return;

    const { x } = localPoint(event);
    bar.months.forEach((item) => {
      if (fiscalMonths.indexOf(item.month) > -1)
        tmpX.push({
          year: item.year,
          month: item.month,
          value: item.CashBalance,
          xPos: xScale(item.month),
        });
    });

    let _x = x - xScale.step() / 2;
    tmpX.sort((a, b) => Math.abs(_x - a.xPos) - Math.abs(_x - b.xPos));

    pLeft = (tmpX ? tmpX[0].xPos : 0) + xScale.step() / 2;
    pTop = tmpX ? yScale(tmpX[0].value) : 0;

    top = event.nativeEvent.layerY + 25;
    left = event.nativeEvent.layerX + 115;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 30
        : event.nativeEvent.layerX + 115;
    dataObj = bar;
    dataObj['isBar'] = isBar;
    dataObj['pLeft'] = pLeft;
    dataObj['pTop'] = pTop;
    dataObj['year'] = tmpX[0].year;
    dataObj['month'] = tmpX[0].month;
    dataObj['value'] = tmpX[0].value;

    showTooltip({
      tooltipData: dataObj,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [defaultMonth, defaultStartMonth, showTooltip]);

  const resetDrag = () => {
    setX(0);
    setIX(0);
    setIY(0);
    setRX(0);
    setRY(0);
    setRW(0);
    setRH(0);
    setCanvasIndex(-1);
    setDragFlag(false);
    setIsMouseDown(false);
    dragChartRef.current = null;
  };

  const getDragCoords = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const selectByDrag = () => {
    const dragChart = dragChartRef.current;
    if (!dragChart || !dragChart.item?.months?.length) return;

    const rectLeft = Math.min(rX, rX + rW);
    const rectRight = Math.max(rX, rX + rW);
    const fiscalMonths = getMonths2(defaultMonth, defaultStartMonth);

    const _selectedTopItems = dragChart.item.months
      .filter((monthItem) => fiscalMonths.indexOf(monthItem.month) > -1)
      .filter((monthItem) => {
        const bandLeft = dragChart.xScale(monthItem.month) ?? 0;
        const bandRight = bandLeft + dragChart.xScale.step();
        return bandLeft < rectRight && bandRight > rectLeft;
      })
      .map((monthItem) => ({
        year: monthItem.year,
        month: monthItem.month,
        left: (dragChart.xScale(monthItem.month) ?? 0) + dragChart.xScale.step() / 2,
        top: dragChart.yScale(monthItem.CashBalance),
      }));

    const dimDateArray = _selectedTopItems.map((item) =>
      printFormat.dateYYYYMMDD(
        item.month + 1 >= defaultStartMonth ? item.year - 1 : item.year,
        item.month + 1,
      ),
    );

    updateFilter({
      selectedTopItems: _selectedTopItems,
      dimDate: dimDateArray,
      selectedBottomItems: [],
    });
    cashflowMiddleRequest(dimDateArray);
  };

  const onSvgMouseDown = (event, item, xScale, yScale) => {
    if (event.button !== 0) return;

    const target = event.target;
    if (
      target &&
      (target.tagName === 'text' ||
        target.getAttribute('data-no-drag') === 'true' ||
        (target.closest && target.closest('[data-no-drag="true"]')))
    ) {
      return;
    }

    const pos = getDragCoords(event);
    dragStartRef.current = pos;
    dragChartRef.current = { item, xScale, yScale };
    setIsMouseDown(true);
    setX(pos.x);
    setIX(pos.x);
    setIY(pos.y);
    setRX(pos.x);
    setRY(pos.y);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseMove = (event) => {
    if (!isMouseDown || !dragFlag || event.buttons !== 1) return;

    const pos = getDragCoords(event);
    const start = dragStartRef.current;
    setX(pos.x);
    setRX(Math.min(start.x, pos.x));
    setRY(Math.min(start.y, pos.y));
    setRW(Math.abs(start.x - pos.x));
    setRH(Math.abs(start.y - pos.y));
  };

  const onSvgMouseUp = () => {
    const dragThreshold = 5;
    const didDrag = rW > dragThreshold || rH > dragThreshold;

    setIsMouseDown(false);
    if (!dragFlag) return;

    if (didDrag) {
      selectByDrag();
    } else {
      deSelectAll();
    }

    resetDrag();
  };

  const onSvgMouseLeave = () => {
    setIsMouseDown(false);
    if (dragFlag) resetDrag();
    hideTooltipHandler();
  };

  const handleLineSelect = useCallback((event, item, xScale, yScale) => {
    let selected = [];
    let fiscalMonths = getMonths2(defaultMonth, defaultStartMonth);
    if (item.months === undefined) return;

    const { x } = localPoint(event);
    item.months.forEach((item) => {
      if (fiscalMonths.indexOf(item.month) > -1)
        selected.push({
          year: item.year,
          month: item.month,
          value: item.CashBalance,
          xPos: xScale(item.month),
        });
    });
    let _x = x - xScale.step() / 2;
    selected.sort((a, b) => Math.abs(_x - a.xPos) - Math.abs(_x - b.xPos));

    let left = (selected ? selected[0].xPos : 0) + xScale.step() / 2;
    let top = selected ? yScale(selected[0].value) : 0;

    let newSelectedTopItems = [...selectedTopItems];
    let dimDateArray = [...dimDate];

    if (event.shiftKey) {
      let index = -1;
      for (let i = 0; i < newSelectedTopItems.length; i++) {
        if (
          newSelectedTopItems[i].month === selected[0].month &&
          newSelectedTopItems[i].year === selected[0].year
        ) {
          index = i;
          break;
        }
      }

      if (index === -1) {
        newSelectedTopItems.push({
          year: selected[0].year,
          month: selected[0].month,
          left: left,
          top: top,
        });
      } else {
        newSelectedTopItems.splice(index, 1);
      }
    } else {
      newSelectedTopItems = [];
      newSelectedTopItems.push({
        year: selected[0].year,
        month: selected[0].month,
        left: left,
        top: top,
      });
    }

    dimDateArray = [];
    newSelectedTopItems.forEach((item) => {
      let _DimDate = printFormat.dateYYYYMMDD(
        item.month + 1 >= defaultStartMonth
          ? item.year - 1
          : item.year,
        item.month + 1,
      );
      dimDateArray.push(_DimDate);
    });

    updateFilter({
      selectedTopItems: newSelectedTopItems,
      dimDate: dimDateArray,
      selectedBottomItems: [],
    });
    cashflowMiddleRequest(dimDateArray);
  }, [selectedTopItems, dimDate, defaultMonth, defaultStartMonth, updateFilter, cashflowMiddleRequest]);

  const hideTooltipHandler = useCallback(() => {
    if (isMountedRef.current) {
      tooltipTimeout = setTimeout(() => {
        if (isMountedRef.current) {
          if (hoverBar) hoverBar.classList.remove('barHover');
          setHoverBar(null);
          hideTooltip();
        }
      }, 300);
    }
  }, [hoverBar, hideTooltip]);

  // Memoize the adjusted min/max line values to prevent re-renders
  const adjustedLines = useMemo(() => {
    let displayMinLine = minLine >= 0 ? 0 : minLine;
    let displayMaxLine = maxLine;
    
    let negcheck = Math.abs(displayMinLine);
    if (displayMaxLine > 1000000 || negcheck > 1000000) {
      displayMinLine = Math.floor(displayMinLine / 500000) * 500000;
      displayMaxLine = Math.ceil(displayMaxLine / 500000) * 500000;
    } else if (displayMaxLine > 500000 || negcheck > 500000) {
      displayMinLine = Math.floor(displayMinLine / 250000) * 250000;
      displayMaxLine = Math.ceil(displayMaxLine / 250000) * 250000;
    } else if (displayMaxLine > 250000 || negcheck > 250000) {
      displayMinLine = Math.floor(displayMinLine / 100000) * 100000;
      displayMaxLine = Math.ceil(displayMaxLine / 100000) * 100000;
    } else if (displayMaxLine > 100000 || negcheck > 100000) {
      displayMinLine = Math.floor(displayMinLine / 25000) * 25000;
      displayMaxLine = Math.ceil(displayMaxLine / 25000) * 25000;
    } else {
      displayMinLine = Math.floor(displayMinLine / 10000) * 10000;
      displayMaxLine = Math.ceil(displayMaxLine / 10000) * 10000;
    }
    
    return { displayMinLine, displayMaxLine };
  }, [minLine, maxLine]);

  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    window.addEventListener('resize', onResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  useEffect(() => {
    if (isMountedRef.current) {
      prepareData();
    }
  }, [topData, prepareData]);

  // Render logic
  data.sort((a, b) => b.year - a.year);

  const width = window.innerWidth - 20;
  const height = 250;
  const margin = {
    top: 10,
    right: 0,
    bottom: 10,
    left: xTransform,
  };

  const { pageYOffset } = getParams(window, width, height, margin);
  let localHeight = 100;
  let localAxisHeight = 25;
  if (data.length >= 3) {
    localHeight = 150;
    localAxisHeight = 100;
  }

  let axisScale = scaleLinear({
    domain: [adjustedLines.displayMinLine, adjustedLines.displayMaxLine],
    range: [localHeight, 0],
  });

  let data_length = data.length > 0 ? data[0].months.length : 0;

  return (
    <div>
      <div className="subtitle mb-5">Cash Balance vs Safety Bank Range</div>

      <Grid container>
        <svg
          width={xTransform}
          height={localHeight + 20}
          style={{ overflow: 'visible' }}
        >
          <Group top={margin.top}>
            <AxisLeft
              left={xTransform}
              scale={axisScale}
              stroke="black"
              tickStroke="black"
              numTicks={5}
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dx: '-0.23em',
                dy: '0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />
          </Group>
        </svg>
        <Grid
          container
          size={12}
          style={{
            display: 'flex',
            position: 'relative',
            width: width - xTransform,
          }}
        >
          {data.map((item, key) => {
            let maxWidth = width - xTransform;
            let fiscalMonths = getMonths2(defaultMonth, defaultStartMonth);
            // scales
            let xScale = scaleBand()
              .domain(item.months.map(xSelector))
              .rangeRound([0, maxWidth])
              .padding(0);

            let yScale;
            yScale = axisScale;

            let validMonths = [];
            item.months.forEach((d, k) => {
              if (d.CashBalance !== 0 && fiscalMonths.indexOf(d.month) > -1)
                validMonths[k] = d;
            });

            let keys = [];
            keys.push('CashBalance');

            return (
              <Grid container key={key}>
                <svg
                  width={maxWidth}
                  height={localHeight + 10}
                  style={{ overflow: 'visible' }}
                  onMouseMove={onSvgMouseMove}
                  onMouseUp={onSvgMouseUp}
                  onMouseLeave={onSvgMouseLeave}
                >
                  <rect
                    x={0}
                    y={0}
                    width={maxWidth}
                    height={localHeight + 10}
                    fill="transparent"
                    onMouseDown={(event) => onSvgMouseDown(event, item, xScale, yScale)}
                    onMouseUp={onSvgMouseUp}
                    onMouseMove={(event) => {
                      onSvgMouseMove(event);
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      showTooltipHandler(event, item, xScale, yScale);
                    }}
                    onTouchMove={(event) => {
                      if (tooltipTimeout) clearTimeout(tooltipTimeout);
                      showTooltipHandler(event, item, xScale, yScale);
                    }}
                    onTouchEnd={(event) => {
                      tooltipTimeout = setTimeout(() => {
                        hideTooltipHandler();
                      }, 300);
                    }}
                  />
                  <defs>
                    <clipPath id="cashpositive">
                      <rect
                        x={0}
                        y={0}
                        width={width}
                        height={
                          yScale(safetyLine) > 0 ? yScale(safetyLine) : 0
                        }
                      />
                    </clipPath>
                    <clipPath id="cashnegative">
                      <rect
                        x={0}
                        y={yScale(safetyLine) > 0 ? yScale(safetyLine) : 0}
                        width={width}
                        height={height}
                      />
                    </clipPath>
                  </defs>
                  <Group top={margin.top}>
                    <LinePath
                      data={[
                        { x: 0, y: yScale(safetyLine) },
                        { x: maxWidth, y: yScale(safetyLine) },
                      ]}
                      x={(d) => (d.x ? d.x : 0)}
                      y={(d) => (d.y ? d.y : 0)}
                      stroke="#000000"
                      strokeDasharray="6,2"
                    />

                    <LinePath
                      data={validMonths}
                      x={(d) => xScale(xSelector(d)) + xScale.step() / 2 - 10}
                      y={(d) => yScale(ySelector(d))}
                      strokeWidth={data_length === 1 ? 6 : 3}
                      stroke={positiveActiveColor}
                      strokeLinecap="round"
                      fill="transparent"
                      clipPath="url(#cashpositive)"
                      onClick={(event) => {
                        handleLineSelect(event, item, xScale, yScale);
                      }}
                    />
                    <LinePath
                      data={validMonths}
                      x={(d) => xScale(xSelector(d)) + xScale.step() / 2 - 10}
                      y={(d) => yScale(ySelector(d))}
                      strokeWidth={data_length === 1 ? 6 : 3}
                      stroke={negativeActiveColor}
                      strokeLinecap="round"
                      fill="transparent"
                      clipPath="url(#cashnegative)"
                      onClick={(event) => {
                        handleLineSelect(event, item, xScale, yScale);
                      }}
                    />

                    {tooltipOpen && (
                      <g>
                        <circle
                          cx={tooltipData.pLeft - 10}
                          cy={tooltipData.pTop}
                          r={tooltipData.year === item.year ? 4 : 0}
                          fill={positiveActiveColor}
                          stroke="black"
                          strokeWidth={0}
                          style={{ pointerEvents: 'none' }}
                        />
                      </g>
                    )}

                    {selectedTopItems.map((sel, selectedKey) => {
                      let radius = 0;
                      let strokeWidth = 0;
                      if (sel.year === item.year) {
                        radius = 6;
                        strokeWidth = 3;
                      }
                      return (
                        <g key={selectedKey}>
                          <circle
                            cx={sel.left - 10}
                            cy={sel.top}
                            r={radius}
                            fill={positiveActiveColor}
                            stroke="black"
                            strokeWidth={strokeWidth}
                            style={{ pointerEvents: 'none' }}
                          />
                        </g>
                      );
                    })}
                  </Group>
                  {dragFlag && dragChartRef.current?.item?.year === item.year && (rW > 0 || rH > 0) && (
                    <rect
                      x={rX}
                      y={rY}
                      width={rW}
                      height={rH}
                      fill="lightblue"
                      stroke="#00f3ff"
                      strokeWidth={1}
                      fillOpacity={0.2}
                      pointerEvents="none"
                    />
                  )}
                </svg>

                <svg
                  width={maxWidth + 10}
                  height={localAxisHeight}
                  style={{ overflow: 'visible' }}
                >
                  <rect
                    x={0}
                    y={0}
                    width={maxWidth + 10}
                    height={localAxisHeight}
                    fill="transparent"
                    data-no-drag="true"
                    onClick={deSelectAll}
                  />
                  <Group>
                    <AxisBottom
                      top={0}
                      scale={xScale}
                      hideTicks={true}
                      stroke="black"
                      tickStroke="black"
                    >
                      {(axis) => {
                        const tickLabelSize = 11;
                        const tickRotate = data.length > 2 ? 270 : 0;
                        const tickColor = 'black';

                        return (
                          <g className="my-custom-bottom-axis">
                            <Line
                              from={axis.axisFromPoint}
                              to={axis.axisToPoint}
                              stroke={tickColor}
                            />
                            {axis.ticks.map((tick, i) => {
                              const tickX = tick.to.x;
                              const tickY =
                                tick.to.y +
                                tickLabelSize +
                                axis.tickLength -
                                12;
                              return (
                                <Group
                                  key={`vx-tick-${tick.value}-${i}`}
                                  className="vx-axis-tick"
                                >
                                  <text
                                    transform={`translate(${tickX}, ${tickY}) rotate(${tickRotate})`}
                                    fontSize={tickLabelSize}
                                    textAnchor="end"
                                    fill={
                                      _.findIndex(selectedTopItems, {
                                        month: tick.formattedValue,
                                      }) > -1
                                        ? activeLabelColor
                                        : tickColor
                                    }
                                    onClick={(event) =>
                                      handleLineSelect(
                                        event,
                                        item,
                                        xScale,
                                        yScale,
                                      )
                                    }
                                  >
                                    {data.length > 5
                                      ? tick.formattedValue % 2 === 0
                                        ? enMonths[tick.formattedValue]
                                        : ''
                                      : enMonths[tick.formattedValue]}
                                  </text>
                                </Group>
                              );
                            })}
                          </g>
                        );
                      }}
                    </AxisBottom>
                  </Group>
                </svg>
              </Grid>
            );
          })}
        </Grid>
      </Grid>

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop + pageYOffset}
          left={tooltipLeft}
        >
          {tooltipData.isBar ? (
            <div className="color-black">
              <div className="pdv-10">
                <strong>{enMonths[tooltipData.month]}</strong>
              </div>
              <div className="ft-12">
                <strong>${thousandFormat(tooltipData.value)}</strong>
              </div>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </div>
  );
};

TopChart.propTypes = {
  topData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  updateFilter: PropTypes.func.isRequired,
  cashflowMiddleRequest: PropTypes.func.isRequired,
  dimDate: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
};

export default TopChart;
